import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Flex, Text, Button, Icon, EditableText, notification } from '@nula/core-ui';
import { useNavigateToEntity } from '@/hooks/navigation';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { useWorkspaceCollections } from '@/lib/sync/hooks/use-workspace-collections';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { canEdit as canEditAccess, canDelete as canDeleteAccess, canRemoveSelf as canRemoveSelfAccess } from '@/lib/sharing/utils/permissions';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import { updateConversation, deleteConversation } from '@/features/assistant/api/conversations';
import { useLiveRecording } from '@/lib/recordings/use-live-recording';
import { RecordingDot } from '@/components/recording-dot/recording-dot';
import { GeneratingDot } from '@/components/generating-dot/generating-dot';
import { isTerminal, runsForEntity, useTemplateRunStore } from '@/stores/use-template-run-store';
import type { RecentEntity } from '@/utils/select-recent-entities';
import { RecentsItemMenu } from './recents-item-menu';
import styles from './sidebar.module.css';

const DOC_KINDS = new Set(['document', 'canvas']);

interface RecentsRowProps {
  entity: RecentEntity;
}

/**
 * One Recents sidebar row: kind icon + title (click navigates) plus a
 * hover-revealed overflow menu. Owns the inline-rename state and applies the
 * per-kind capability matrix, routing each action to the kind-appropriate
 * service method (doc / collection / conversation).
 */
export function RecentsRow({ entity }: RecentsRowProps) {
  const navigateToEntity = useNavigateToEntity();
  const service = useWorkspaceService();
  const allDocs = useWorkspaceDocs();
  const allCollections = useWorkspaceCollections();
  const [renaming, setRenaming] = useState(false);
  const live = useLiveRecording(entity.id);
  const isRecording = live?.phase === 'capturing';
  const isGenerating = useTemplateRunStore((s) =>
    runsForEntity(s.runs, entity.id).some((run) => !isTerminal(run.stage)));

  const isDoc = DOC_KINDS.has(entity.kind);
  const isCollection = entity.kind === 'collection';
  const isConversation = entity.kind === 'conversation';

  // Permission: documents carry access in the docs list, so shared read-only
  // docs correctly hide edit/delete. Conversations aren't in either list
  // (they're always the user's own) — default them to editable/deletable.
  // Collections do carry access too, but only removeSelf reads from that list
  // below — canEdit/canDelete keep the pre-sharing default of "true" here to
  // avoid changing collection behavior outside this fix's scope.
  const doc = allDocs.find((d) => d.id === entity.id);
  const canEdit = isDoc ? (doc?.access ? canEditAccess(doc.access) : false) : true;
  const canDelete = isDoc ? (doc?.access ? canDeleteAccess(doc.access) : false) : true;
  const collection = allCollections.find((c) => c.id === entity.id);
  const removeSelfAccess = isDoc ? doc?.access : isCollection ? collection?.access : undefined;
  // removeSelf defaults to false (not true like canEdit/canDelete above) — an
  // entity we know nothing about must not offer to remove itself.
  const canRemoveSelf = removeSelfAccess ? canRemoveSelfAccess(removeSelfAccess) : false;

  // ── Capability matrix ──────────────────────────────────────────────────────
  const showAddToCollection = isDoc && canEdit; // only docs can be filed; needs edit
  const showRename = canEdit;
  const showArchive = (isDoc || isCollection) && canDelete; // conversations have no archive
  const showDelete = canDelete;
  const showRemoveSelf = canRemoveSelf;

  // ── Per-kind action routing ─────────────────────────────────────────────────
  const commitRename = (title: string) => {
    if (isDoc) service.renameDoc(entity.id, title);
    else if (isCollection) service.renameCollection(entity.id, title);
    else if (isConversation) updateConversation(entity.id, { title }).catch((err) => Sentry.captureException(err));
  };

  const handleArchive = () => {
    if (isCollection) service.archiveCollection(entity.id, true);
    else service.archiveDoc(entity.id, true);
    let instance: { destroy: () => void } | null = null;
    instance = notification.success({
      message: 'Moved to trash',
      description: entity.title || 'Untitled',
      btn: (
        <Button
          size="sm"
          variant="text"
          onClick={() => {
            if (isCollection) service.archiveCollection(entity.id, false);
            else service.archiveDoc(entity.id, false);
            instance?.destroy();
          }}
        >
          Undo
        </Button>
      ),
      duration: 5000,
    });
  };

  const handleDelete = () => {
    const p = isCollection
      ? service.deleteCollectionPermanently(entity.id)
      : isConversation
      ? deleteConversation(entity.id)
      : service.deleteDocPermanently(entity.id);
    p.catch((err) => Sentry.captureException(err));
  };

  const handleAddToCollection = (collectionId: string) => {
    service.addToCollection(entity.id, collectionId);
  };

  const handleRemoveSelf = () => {
    service.leaveEntity(entity.id).catch((err) => Sentry.captureException(err));
  };

  return (
    <Flex align="center" className={styles.recentsItem}>
      <Button
        variant="text"
        className={styles.recentsItemTitleBtn}
        icon={<Icon name={getEntityKindMeta(entity.kind).icon} size={16} weight={300} />}
        onClick={() => navigateToEntity(entity.id)}
      >
        <Flex align="center" className={styles.recentsItemTitleRow}>
          {renaming ? (
            <EditableText
              value={entity.title || 'Untitled'}
              editing
              onChange={(val) => { if (val.trim() && val.trim() !== entity.title) commitRename(val.trim()); }}
              onEditingChange={(v) => { if (!v) setRenaming(false); }}
              size="sm"
              renderDisplay={(val) => <Text ellipsis className={styles.recentsItemText}>{val}</Text>}
            />
          ) : (
            <Text ellipsis className={styles.recentsItemText}>
              {entity.title || 'Untitled'}
            </Text>
          )}
          {isRecording && <RecordingDot className={styles.recentsItemRecordingDot} />}
          {isGenerating && !isRecording && <GeneratingDot className={styles.recentsItemRecordingDot} />}
        </Flex>
      </Button>
      <Flex className={styles.recentsItemActions} onClick={(e) => e.stopPropagation()}>
        <RecentsItemMenu
          showAddToCollection={showAddToCollection}
          showRename={showRename}
          showArchive={showArchive}
          showDelete={showDelete}
          showRemoveSelf={showRemoveSelf}
          onOpen={() => navigateToEntity(entity.id)}
          onAddToCollection={handleAddToCollection}
          onRename={() => setRenaming(true)}
          onArchive={handleArchive}
          onDelete={handleDelete}
          onRemoveSelf={handleRemoveSelf}
        />
      </Flex>
    </Flex>
  );
}
